import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningPage from './WarningPage';
import RetryNotice from './RetryNotice';
import WarningLoading from './WarningLoading';
import FlagSummary from './FlagSummary';
import apiRequest from '../../api/request';
import { downloadCSV, makeCSV } from '../../components/CustomTableWithPagination/csvExport';
import dayjs from 'dayjs';
import useConfigAccess from './useConfigAccess';

const PHASE_LABEL = { 1: 'Onboarding', 2: 'Demo & Admission', 3: 'Tracking & Retention' };
const PHASE_ORDER = [1, 2, 3];

// What a lead's hours are counted from: Verify basics from the moment it was assigned to the CRO, every later stage
// from the moment the previous stage was completed (whether or not the CRO has pressed Start on this one yet).
const CLOCK_LABEL = {
  assigned: 'since assigned to CRO',
  previous_stage_completed: 'since the previous stage was completed',
  stage_started: 'since the stage was started',
};
const waitingSince = (item) => (item.clockStartedAt ? dayjs(item.clockStartedAt).format('DD MMM YYYY, HH:mm') : '-');
const clockCaption = (item) => [CLOCK_LABEL[item.clockFrom], item.stageStarted === false ? 'not started yet' : null].filter(Boolean).join(' · ');

const ITEM_CSV_HEADERS = [
  { field: 'stageLabel', headerName: 'Stage' },
  { field: 'studentName', headerName: 'Student' },
  { field: 'courseName', headerName: 'Course' },
  { field: 'croName', headerName: 'CRO' },
  { field: 'waitingSince', headerName: 'Waiting Since' },
  { field: 'countedFrom', headerName: 'Counted From' },
  { field: 'hoursPending', headerName: 'Hours Pending' },
  { field: 'isOverdue', headerName: 'Overdue' },
];

// one cell for both tables: the date, and a grey line saying what it is counted from
const WaitingSinceCell = ({ item }) => (
  <>
    <Typography variant="body2">{waitingSince(item)}</Typography>
    <Typography variant="caption" color="text.secondary">{clockCaption(item)}</Typography>
  </>
);

const exportItems = (items, fileNameSuffix) => {
  const csv = makeCSV({
    headers: ITEM_CSV_HEADERS,
    rows: items.map((i) => ({ ...i, courseName: i.courseName || '', waitingSince: waitingSince(i), countedFrom: clockCaption(i), isOverdue: i.isOverdue ? 'Yes' : 'No' })),
  });
  downloadCSV(csv, `CRO_Pending_Tasks_${fileNameSuffix}_${dayjs().format('YYYY-MM-DD_HH-mm')}.csv`);
};

// CRO Pending Tasks - what's open at EACH workflow stage right now, and how long it's been waiting on the CRO.
// The hours are counted from assignment (stage 1) or from the previous stage's completion (stage 2+) - see croStageClock.js.
// One section per stage (grouped by phase), not one row per CRO - this is a "where is the workflow backing
// up" view. Threshold is per-stage and configurable (see cro_pending_tasks_config.controller.js). No month
// picker - this is always a live snapshot, never a historical period. Viewer scope is server-side: a CRO sees
// only their own items, their reporting head sees their team's, Finance/Top Management/Technical see everyone.
const CroPendingTasksDashboard = () => {
  const [searchParams] = useSearchParams();
  const stageFilter = searchParams.get('stage') ? Number(searchParams.get('stage')) : null;
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ totalEvaluated: 0, warningCount: 0 });
  const [enrolledFrom, setEnrolledFrom] = useState(null); // 'YYYY-MM-DD': only enrolments from that day on are counted (server constant); null = all
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0); // Retry re-runs just this request

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiRequest({ url: 'warning-dashboard/cro-pending-tasks-dashboard', method: 'get' })
      .then((res) => {
        if (cancelled) return;
        // apiRequest resolves even on a failed call (src/api/request.js), so this status check is required -
        // a .catch alone would never fire and this would silently show "nothing pending" instead of the error.
        if (res?.status !== 'success') {
          setError(res?.message || 'Could not load CRO Pending Tasks');
          return;
        }
        setRows(res?.data?.rows || []);
        setSummary({ totalEvaluated: res?.data?.totalEvaluated || 0, warningCount: res?.data?.warningCount || 0 });
        setEnrolledFrom(res?.data?.enrolledFrom || null);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || 'Could not load CRO Pending Tasks');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const byPhase = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const phaseNo = r.phaseNo;
      if (!map.has(phaseNo)) map.set(phaseNo, []);
      map.get(phaseNo).push(r);
    }
    for (const stages of map.values()) stages.sort((a, b) => a.stageNo - b.stageNo);
    return map;
  }, [rows]);

  const focusedStage = stageFilter ? rows.find((r) => r.stageNo === stageFilter) : null;

  const allItemsFlat = useMemo(
    () => rows.flatMap((stage) => stage.items.map((i) => ({ ...i, stageLabel: `${stage.stageNo} · ${stage.stageName}` }))),
    [rows],
  );

  const handleExport = () => {
    if (focusedStage) {
      exportItems(
        focusedStage.items.map((i) => ({ ...i, stageLabel: `${focusedStage.stageNo} · ${focusedStage.stageName}` })),
        `Stage_${focusedStage.stageNo}`,
      );
    } else {
      exportItems(allItemsFlat, 'All_Stages');
    }
  };

  const { canConfigure } = useConfigAccess();

  return (
    <WarningPage
      title={focusedStage ? `CRO Pending Tasks - Stage ${focusedStage.stageNo} · ${focusedStage.stageName}` : 'CRO Pending Tasks'}
      actions={
        <Box display="flex" gap={1}>
          {stageFilter && (
            <Button component={Link} to="/dashboard/warning-dashboard/cro-pending-tasks" variant="text">View all stages</Button>
          )}
          <Button
            variant="outlined"
            onClick={handleExport}
            disabled={loading || !!error || (focusedStage ? focusedStage.items.length === 0 : allItemsFlat.length === 0)}
          >
            Export CSV
          </Button>
          <Button component={Link} to="/dashboard/warning-dashboard" variant="text">Back to Warning Dashboard</Button>
          {canConfigure('cro-pending-tasks-config') && (
            <Button component={Link} to="/dashboard/warning-dashboard/cro-pending-tasks-config" variant="outlined">Configure Thresholds</Button>
          )}
        </Box>
      }
    >
      {loading && <WarningLoading />}

      {!loading && error && <RetryNotice message={error} onRetry={() => setReloadKey((k) => k + 1)} />}

      {!loading && !error && enrolledFrom && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Counting enrolments from {dayjs(enrolledFrom).format('D MMM YYYY')} onwards - leads enrolled before that day are not checked here.
        </Typography>
      )}

      {!loading && !error && stageFilter && !focusedStage && (
        <Typography color="text.secondary">Stage {stageFilter} not found.</Typography>
      )}

      {!loading && !error && focusedStage && (
        <Box>
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ mb: 2 }}>
            <Chip size="small" label={`${focusedStage.pendingCount} pending`} />
            <Chip size="small" color={focusedStage.value > 0 ? 'error' : 'success'} label={`${focusedStage.value} overdue`} />
            <Chip size="small" variant="outlined" label={`threshold ${focusedStage.thresholdHours}h`} />
          </Box>
          {focusedStage.items.length === 0 ? (
            <Typography color="text.secondary">Nothing pending at this stage right now, in your scope.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#FAF8F3' }}>
                    <TableCell align="center"><strong>Status</strong></TableCell>
                    <TableCell><strong>Student</strong></TableCell>
                    <TableCell><strong>Course</strong></TableCell>
                    <TableCell><strong>CRO</strong></TableCell>
                    <TableCell><strong>Waiting since</strong></TableCell>
                    <TableCell align="right"><strong>Hours pending</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {focusedStage.items.map((item) => (
                    <TableRow key={item.courseEnrollmentId} hover>
                      <TableCell align="center">
                        {item.isOverdue ? (
                          <WarningAmberIcon fontSize="small" sx={{ color: 'error.main', display: 'block', mx: 'auto' }} />
                        ) : (
                          <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main', display: 'block', mx: 'auto' }} />
                        )}
                      </TableCell>
                      <TableCell>{item.studentName}</TableCell>
                      <TableCell>{item.courseName || '-'}</TableCell>
                      <TableCell>{item.croName}</TableCell>
                      <TableCell><WaitingSinceCell item={item} /></TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={item.isOverdue ? 700 : 400} color={item.isOverdue ? 'error.main' : 'text.primary'}>
                          {item.hoursPending}h
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {!loading && !error && !stageFilter && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          What's pending at each stage of the retention workflow, and how long it's been waiting on the CRO. Verify basics is counted from
          when the lead was assigned to the CRO; every later stage is counted from when the previous stage was completed. A lead appears here
          once its stage has been waiting longer than the stage's threshold.
        </Typography>
      )}
      {!loading && !error && !stageFilter && (
        <FlagSummary warningCount={summary.warningCount} totalEvaluated={summary.totalEvaluated} unit=" stages" />
      )}

      {!loading && !error && !stageFilter && PHASE_ORDER.map((phaseNo) => {
        const stages = byPhase.get(phaseNo) || [];
        if (stages.length === 0) return null;
        return (
          <Box key={phaseNo} sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>{PHASE_LABEL[phaseNo] || `Phase ${phaseNo}`}</Typography>
            <Stack gap={1.5}>
              {stages.map((stage) => (
                <Paper key={stage.stageNo} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
                    {stage.isWarning ? (
                      <WarningAmberIcon fontSize="small" sx={{ color: 'error.main' }} />
                    ) : (
                      <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main' }} />
                    )}
                    <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 180 }}>
                      {stage.stageNo} · {stage.stageName}
                    </Typography>
                    <Chip size="small" label={`${stage.pendingCount} pending`} />
                    <Chip size="small" color={stage.value > 0 ? 'error' : 'success'} label={`${stage.value} overdue`} />
                    <Chip size="small" variant="outlined" label={`threshold ${stage.thresholdHours}h`} />
                    {stage.items.length > 0 && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => exportItems(stage.items.map((i) => ({ ...i, stageLabel: `${stage.stageNo} · ${stage.stageName}` })), `Stage_${stage.stageNo}`)}
                      >
                        Export CSV
                      </Button>
                    )}
                  </Stack>

                  {stage.items.length > 0 && (
                    <TableContainer sx={{ mt: 1.5 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Student</strong></TableCell>
                            <TableCell><strong>Course</strong></TableCell>
                            <TableCell><strong>CRO</strong></TableCell>
                            <TableCell><strong>Waiting since</strong></TableCell>
                            <TableCell align="right"><strong>Hours pending</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {stage.items.map((item) => (
                            <TableRow key={item.courseEnrollmentId} hover>
                              <TableCell>{item.studentName}</TableCell>
                              <TableCell>{item.courseName || '-'}</TableCell>
                              <TableCell>{item.croName}</TableCell>
                              <TableCell><WaitingSinceCell item={item} /></TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight={item.isOverdue ? 700 : 400} color={item.isOverdue ? 'error.main' : 'text.primary'}>
                                  {item.hoursPending}h
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Paper>
              ))}
            </Stack>
          </Box>
        );
      })}
    </WarningPage>
  );
};

export default CroPendingTasksDashboard;
